import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { collection, doc, getDoc, getDocs, writeBatch } from "firebase/firestore";
import PageHeader from "../components/PageHeader";
import { db } from "../firebase";
import { useSurveyRole } from "../hooks/useSurveyRole";
import { findElementByName, getAnswerVals, SurveyJSON } from "../hooks/useNetworkData";

type NetworkConfig = {
  id: string;
  source: string;
  target: string;
  updatedAt?: string;
};

type ResponseDoc = {
  id: string;
  answers: any;
};

const PANEL_TYPES = new Set(["panel", "paneldynamic"]);
const HOW_TYPES = new Set(["checkbox", "tagbox", "radiogroup", "rating"]);

const normalizeName = (val: string) => val.replace(/\s+/g, " ").trim();

const getComment = (container: any, baseName: string): string => {
  const a = container?.[`${baseName}-Comment`];
  const b = container?.[`${baseName}Comment`];
  return (typeof a === "string" ? a : typeof b === "string" ? b : "") as string;
};

const coalesceOther = (val: string | undefined, comment?: unknown): string => {
  const v = (val ?? "").trim();
  if (!v) return "";
  const isOther = /^other\b|please\s*specify/i.test(v);
  const c = (typeof comment === "string" ? comment : "").trim();
  if (isOther) return c || "";
  return v;
};

const pickOrgField = (panelItem: any, howFieldNames: Set<string>): string | undefined => {
  if (!panelItem || typeof panelItem !== "object") return undefined;
  const keys = Object.keys(panelItem);
  let bestKey: string | undefined;
  let bestScore = -Infinity;
  for (const k of keys) {
    const v = panelItem[k];
    if (typeof v !== "string" || !v.trim()) continue;
    if (howFieldNames.has(k)) continue;
    if (/(other|comment)/i.test(k)) continue;
    let score = 0;
    if (/(^|[^a-z])(organisation|organization|org)([^a-z]|$)/i.test(k)) score += 50;
    if (/(^|[^a-z])name([^a-z]|$)/i.test(k)) score += 30;
    if (/(work together|what.*work)/i.test(k)) score -= 20;
    score -= Math.min(k.length, 40) * 0.1;
    if (score > bestScore) {
      bestScore = score;
      bestKey = k;
    }
  }
  if (!bestKey) {
    bestKey = keys.find(
      (k) =>
        typeof panelItem[k] === "string" &&
        panelItem[k].trim() &&
        !howFieldNames.has(k) &&
        !/(other|comment)/i.test(k)
    );
  }
  return bestKey;
};

const extractConfigsFromData = (docId: string, data?: any): NetworkConfig[] => {
  if (!data) return [];
  const raw = Array.isArray(data.configs) ? data.configs : [];
  const parsed = raw
    .map((cfg: any, idx: number) => {
      if (!cfg?.source || !cfg?.target) return null;
      return {
        id: typeof cfg.id === "string" && cfg.id.trim() ? cfg.id : `${docId}-${idx}`,
        source: cfg.source,
        target: cfg.target,
        updatedAt: typeof cfg.updatedAt === "string" ? cfg.updatedAt : undefined,
      };
    })
    .filter(Boolean) as NetworkConfig[];

  if (data.source && data.target) {
    parsed.push({
      id: `${docId}-legacy`,
      source: data.source,
      target: data.target,
      updatedAt: data.updatedAt,
    });
  }

  return parsed.sort((a, b) => {
    const ta = a.updatedAt ? Date.parse(a.updatedAt) : 0;
    const tb = b.updatedAt ? Date.parse(b.updatedAt) : 0;
    return tb - ta;
  });
};

const getHowFieldNames = (surveyJson: SurveyJSON | undefined, target: string) => {
  const howFieldNames = new Set<string>();
  if (!surveyJson || !target) return howFieldNames;
  const panel = findElementByName(surveyJson, target);
  const tpls = panel?.templateElements || [];
  tpls.forEach((el) => {
    if (!el?.name) return;
    const type = (el.type || "").toLowerCase();
    if (!HOW_TYPES.has(type)) return;
    howFieldNames.add(el.name);
  });
  return howFieldNames;
};

const isPanelTarget = (surveyJson: SurveyJSON | undefined, target: string) => {
  const el = findElementByName(surveyJson, target);
  const type = (el?.type || "").toLowerCase();
  return PANEL_TYPES.has(type) || (el?.templateElements?.length ?? 0) > 0;
};

export default function StakeholderRename() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, loading: roleLoading } = useSurveyRole(id);

  const [surveyTitle, setSurveyTitle] = useState<string>("");
  const [surveyJson, setSurveyJson] = useState<SurveyJSON | undefined>();
  const [source, setSource] = useState<string>("");
  const [target, setTarget] = useState<string>("");
  const [responses, setResponses] = useState<ResponseDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rightText, setRightText] = useState<string>("");
  const [applying, setApplying] = useState<boolean>(false);
  const [appliedCount, setAppliedCount] = useState<number | null>(null);

  useEffect(() => {
    if (!id || !isAdmin) return;
    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const surveySnap = await getDoc(doc(db, "surveys", id));
        if (!surveySnap.exists()) throw new Error("Survey not found.");
        const surveyData = surveySnap.data() as any;
        const json =
          (surveyData?.json as SurveyJSON) ??
          (surveyData?.creatorJson as SurveyJSON) ??
          (surveyData?.definition as SurveyJSON) ??
          (surveyData?.schema as SurveyJSON);
        const title = json?.title || surveyData?.title || "(Untitled Survey)";

        const cfgSnap = await getDoc(doc(db, "networkConfigs", id));
        const configs = cfgSnap.exists()
          ? extractConfigsFromData(cfgSnap.id, cfgSnap.data())
          : [];
        const primary = configs[0];
        if (!primary) {
          throw new Error("No network configuration found for this survey.");
        }

        const respSnap = await getDocs(collection(db, "surveys", id, "responses"));
        const respDocs = respSnap.docs
          .map((d) => {
            const data = d.data() as any;
            if (data?.deleted === true) return null;
            return { id: d.id, answers: data?.answers || {} };
          })
          .filter(Boolean) as ResponseDoc[];

        if (!alive) return;
        setSurveyTitle(title);
        setSurveyJson(json);
        setSource(primary.source);
        setTarget(primary.target);
        setResponses(respDocs);
      } catch (e: any) {
        if (!alive) return;
        setError(e?.message || "Failed to load survey data.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id, isAdmin]);

  const stakeholderList = useMemo(() => {
    if (!responses.length || !source || !target) return [];
    const names: string[] = [];
    const seen = new Set<string>();
    const howFieldNames = getHowFieldNames(surveyJson, target);
    const panelTarget = isPanelTarget(surveyJson, target);

    const pushName = (name: string) => {
      const clean = normalizeName(name);
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      names.push(clean);
    };

    responses.forEach((resp) => {
      const ans = resp.answers || {};
      getAnswerVals(ans, source).forEach(pushName);

      const directTargets = getAnswerVals(ans, target);
      if (directTargets.length) {
        directTargets.forEach(pushName);
        return;
      }

      if (!panelTarget) return;
      const panel = ans?.[target];
      if (!Array.isArray(panel)) return;
      panel.forEach((item) => {
        const orgKey = pickOrgField(item, howFieldNames);
        if (orgKey) {
          const orgComment = getComment(item, orgKey);
          const orgVal = coalesceOther(item?.[orgKey], orgComment);
          if (orgVal) pushName(orgVal);
          return;
        }
        Object.keys(item || {}).forEach((key) => {
          if (howFieldNames.has(key) || /(other|comment)/i.test(key)) return;
          if (typeof item[key] !== "string") return;
          const val = coalesceOther(item[key], getComment(item, key));
          if (val) pushName(val);
        });
      });
    });

    return names;
  }, [responses, source, target, surveyJson]);

  const leftText = stakeholderList.join("\n");
  const lineNumbers = stakeholderList.map((_, i) => String(i)).join("\n");

  const replacementLines = useMemo(() => {
    const lines = rightText.split(/\r?\n/);
    if (lines.length < stakeholderList.length) {
      return [...lines, ...Array(stakeholderList.length - lines.length).fill("")];
    }
    return lines;
  }, [rightText, stakeholderList.length]);

  const mapping = useMemo(() => {
    const map = new Map<string, string>();
    stakeholderList.forEach((original, idx) => {
      const next = normalizeName(replacementLines[idx] || "");
      if (!next || next === original) return;
      map.set(original, next);
    });
    return map;
  }, [stakeholderList, replacementLines]);

  const tooManyLines = replacementLines.length > stakeholderList.length;

  const handleApply = async () => {
    if (!id || mapping.size === 0 || applying || !source || !target) return;
    if (tooManyLines) {
      setError("Paste list has more lines than the original list.");
      return;
    }

    const confirm = window.confirm(
      `You are about to update ${mapping.size} stakeholder names in this survey. Continue?`
    );
    if (!confirm) return;

    setApplying(true);
    setError(null);
    setAppliedCount(null);

    try {
      const howFieldNames = getHowFieldNames(surveyJson, target);
      const panelTarget = isPanelTarget(surveyJson, target);
      const updates: Array<{ id: string; answers: any }> = [];

      const replaceValue = (val: string): { next: string; changed: boolean } => {
        const clean = normalizeName(val);
        if (!clean) return { next: val, changed: false };
        const mapped = mapping.get(clean);
        if (!mapped) return { next: val, changed: false };
        return { next: mapped, changed: mapped !== val };
      };

      const updateComment = (container: any, baseName: string) => {
        let changed = false;
        const keys = [`${baseName}-Comment`, `${baseName}Comment`];
        keys.forEach((k) => {
          const current = container?.[k];
          if (typeof current !== "string") return;
          const { next, changed: didChange } = replaceValue(current);
          if (didChange) {
            container[k] = next;
            changed = true;
          }
        });
        return changed;
      };

      const updateFieldValue = (container: any, fieldKey: string) => {
        let changed = false;
        const current = container?.[fieldKey];
        if (typeof current === "string") {
          const { next, changed: didChange } = replaceValue(current);
          if (didChange) {
            container[fieldKey] = next;
            changed = true;
          }
          if (updateComment(container, fieldKey)) {
            changed = true;
          }
          return changed;
        }
        if (Array.isArray(current) && current.every((v) => typeof v === "string")) {
          const nextArr = current.map((v) => {
            const { next } = replaceValue(String(v));
            return next;
          });
          if (nextArr.some((v, i) => v !== current[i])) {
            container[fieldKey] = nextArr;
            changed = true;
          }
          if (updateComment(container, fieldKey)) {
            changed = true;
          }
        }
        return changed;
      };

      responses.forEach((resp) => {
        const ans = resp.answers || {};
        let changed = false;

        const nextAnswers = { ...ans };
        if (updateFieldValue(nextAnswers, source)) changed = true;

        const directTargets = getAnswerVals(ans, target);
        if (directTargets.length || !panelTarget) {
          if (updateFieldValue(nextAnswers, target)) changed = true;
        } else {
          const panel = nextAnswers?.[target];
          if (Array.isArray(panel)) {
            const nextPanel = panel.map((item) => {
              if (!item || typeof item !== "object") return item;
              const nextItem = { ...item };
              const orgKey = pickOrgField(nextItem, howFieldNames);
              if (orgKey) {
                const current = nextItem?.[orgKey];
                if (typeof current === "string") {
                  const { next, changed: didChange } = replaceValue(current);
                if (didChange) {
                  nextItem[orgKey] = next;
                  changed = true;
                }
              }
              if (updateComment(nextItem, orgKey)) {
                changed = true;
              }
            }
            Object.keys(nextItem).forEach((key) => {
              if (howFieldNames.has(key) || /(other|comment)/i.test(key)) return;
              const current = nextItem?.[key];
              if (typeof current !== "string") return;
                const { next, changed: didChange } = replaceValue(current);
                if (didChange) {
                  nextItem[key] = next;
                  changed = true;
                }
                if (updateComment(nextItem, key)) {
                  changed = true;
                }
              });
              return nextItem;
            });
            if (nextPanel !== panel) {
              nextAnswers[target] = nextPanel;
            }
          }
        }

        if (changed) {
          updates.push({ id: resp.id, answers: nextAnswers });
        }
      });

      let updated = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      const commitBatch = async () => {
        if (batchCount === 0) return;
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      };

      for (const u of updates) {
        batch.update(doc(db, "surveys", id, "responses", u.id), { answers: u.answers });
        batchCount += 1;
        updated += 1;
        if (batchCount >= 450) {
          await commitBatch();
        }
      }
      await commitBatch();

      if (updates.length) {
        setResponses((prev) =>
          prev.map((resp) => {
            const hit = updates.find((u) => u.id === resp.id);
            return hit ? { ...resp, answers: hit.answers } : resp;
          })
        );
      }

      setAppliedCount(updated);
      if (updated === 0) {
        setError("No matching responses were updated. Check the pasted list and try again.");
      }
    } catch (e: any) {
      setError(e?.message || "Failed to update stakeholder names.");
    } finally {
      setApplying(false);
    }
  };

  if (roleLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admins only.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Rename stakeholders"
        subtitle={surveyTitle ? `Survey: ${surveyTitle}` : "Survey stakeholder rename tool"}
        actions={
          <Button variant="outlined" onClick={() => navigate(`/surveys/${id}`)}>
            Back to Survey
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Source question: <strong>{source || "Not set"}</strong> · Target question:{" "}
            <strong>{target || "Not set"}</strong>
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {stakeholderList.length === 0 ? (
            <Alert severity="info">No stakeholder names found for this survey.</Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Paste the updated list on the right. Empty lines keep the original name.
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", gap: 1, flex: "1 1 420px", minWidth: 320 }}>
                  <Box
                    sx={{
                      color: "text.secondary",
                      fontFamily: "monospace",
                      whiteSpace: "pre",
                      lineHeight: 1.6,
                      textAlign: "right",
                      pt: "24px",
                    }}
                  >
                    {lineNumbers}
                  </Box>
                  <TextField
                    label="Original stakeholder list"
                    fullWidth
                    multiline
                    minRows={14}
                    value={leftText}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: "monospace", lineHeight: "1.6" },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 1, flex: "1 1 420px", minWidth: 320 }}>
                  <Box
                    sx={{
                      color: "text.secondary",
                      fontFamily: "monospace",
                      whiteSpace: "pre",
                      lineHeight: 1.6,
                      textAlign: "right",
                      pt: "24px",
                    }}
                  >
                    {lineNumbers}
                  </Box>
                  <TextField
                    label="Replacement list (paste here)"
                    fullWidth
                    multiline
                    minRows={14}
                    value={rightText}
                    onChange={(e) => setRightText(e.target.value)}
                    InputProps={{ style: { fontFamily: "monospace", lineHeight: "1.6" } }}
                    error={tooManyLines}
                    helperText={
                      tooManyLines
                        ? "Too many lines. Remove extra lines to match the original list."
                        : undefined
                    }
                    sx={{
                      "& .MuiInputBase-input": {
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  disabled={mapping.size === 0 || applying || tooManyLines}
                >
                  {applying ? "Updating…" : `Update ${mapping.size} name(s)`}
                </Button>
                {appliedCount !== null && (
                  <Typography variant="body2">
                    Updated {appliedCount} response{appliedCount === 1 ? "" : "s"}.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}
